import { onAnimationEnd } from './motion-utils.js';

const CART_DRAWER_ROOT_SELECTOR = '[data-cart-drawer-root]';
const CART_DRAWER_SELECTOR = '[data-id="cart-drawer"], [data-drawer-id="cart-drawer"]';
const CART_LINE_SELECTOR = '[data-cart-line]';
const AUTO_UPDATE_DEBOUNCE_MS = 450;

let autoUpdateTimeoutId = null;
let cartUpdateInFlight = false;
let queuedCartUpdateForm = null;

const getStoreRoot = () => window.Shopify?.routes?.root || '/';

const getStoreUrl = (path = '') => new URL(path, window.location.origin + getStoreRoot()).toString();

const getCartDrawerRoot = () => document.querySelector(CART_DRAWER_ROOT_SELECTOR);

const getCartDrawer = () => document.querySelector(CART_DRAWER_SELECTOR);

const getCartUpdateForm = () => document.querySelector('form[data-cart-update-form]');

const getCartMessages = (form) => ({
  updating: form?.dataset.messageUpdating || 'Updating...',
  invalidNumber: form?.dataset.messageInvalidNumber || 'Enter a valid quantity.',
  invalidStep: form?.dataset.messageInvalidStep || 'Use a valid quantity increment.',
  minimumQuantity: form?.dataset.messageMinimumQuantity || 'Minimum quantity is {{ min }}.',
  maximumQuantity: form?.dataset.messageMaximumQuantity || 'Maximum quantity is {{ max }}.',
  networkError: form?.dataset.messageNetworkError || 'Network error. Check your connection and try again.',
  genericError: form?.dataset.messageGenericError || 'Unable to update cart. Please try again.',
});

const getLineElementByIndex = (lineIndex, scope = document) =>
  scope.querySelector(`${CART_LINE_SELECTOR}[data-line-index="${lineIndex}"]`);

const getLineFeedbackElement = (lineElement) => lineElement?.querySelector('[data-cart-line-feedback]');

const getLineQuantityInput = (lineElement) => lineElement?.querySelector('[data-cart-quantity-input]');

const parseIntOrDefault = (value, fallback) => {
  const parsed = Number.parseInt(`${value ?? ''}`.trim(), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getQuantityInputValues = (input) => ({
  quantity: parseIntOrDefault(input.value, Number.NaN),
  min: parseIntOrDefault(input.min, 0),
  max: parseIntOrDefault(input.max, null),
  step: parseIntOrDefault(input.step, 1),
});

const triggerAnimationClass = (element, className) => {
  if (!(element instanceof HTMLElement)) return;

  element.classList.add(className);
  void onAnimationEnd(element, () => {
    element.classList.remove(className);
  });
};

const removeDescribedBy = (input, feedbackId) => {
  const describedBy = input.getAttribute('aria-describedby');
  if (!describedBy) return;

  const tokens = describedBy
    .split(/\s+/)
    .filter(Boolean)
    .filter((id) => id !== feedbackId);

  if (tokens.length) {
    input.setAttribute('aria-describedby', tokens.join(' '));
    return;
  }

  input.removeAttribute('aria-describedby');
};

const clearLineFeedback = (lineElement) => {
  if (!(lineElement instanceof HTMLElement)) return;

  const feedback = getLineFeedbackElement(lineElement);
  if (feedback instanceof HTMLElement) {
    feedback.textContent = '';
    feedback.hidden = true;
    feedback.classList.remove('cart-drawer__line-feedback--pending', 'cart-drawer__line-feedback--error');
    feedback.removeAttribute('aria-live');
  }

  const input = getLineQuantityInput(lineElement);
  if (input instanceof HTMLInputElement) {
    input.removeAttribute('aria-invalid');
    if (feedback?.id) {
      removeDescribedBy(input, feedback.id);
    }
  }
};

const clearAllLineFeedback = (scope = document) => {
  const lines = scope.querySelectorAll(CART_LINE_SELECTOR);
  for (const lineElement of lines) {
    clearLineFeedback(lineElement);
  }
};

const setLineFeedback = (lineElement, message, type = 'error') => {
  if (!(lineElement instanceof HTMLElement)) return;

  clearLineFeedback(lineElement);

  const feedback = getLineFeedbackElement(lineElement);
  if (!(feedback instanceof HTMLElement)) return;

  feedback.textContent = message;
  feedback.hidden = false;
  feedback.classList.add(type === 'pending' ? 'cart-drawer__line-feedback--pending' : 'cart-drawer__line-feedback--error');
  feedback.setAttribute('aria-live', type === 'pending' ? 'polite' : 'assertive');
  triggerAnimationClass(feedback, 'fade-enter-active');

  const input = getLineQuantityInput(lineElement);
  if (!(input instanceof HTMLInputElement)) return;
  if (type !== 'error') return;

  input.setAttribute('aria-invalid', 'true');
  if (!feedback.id) return;

  const describedBy = input.getAttribute('aria-describedby') || '';
  if (describedBy.split(/\s+/).includes(feedback.id)) return;

  const nextValue = describedBy.trim().length ? `${describedBy} ${feedback.id}` : feedback.id;
  input.setAttribute('aria-describedby', nextValue);
};

const setLinePending = (lineElement, pending, pendingMessage) => {
  if (!(lineElement instanceof HTMLElement)) return;

  lineElement.classList.toggle('cart-drawer__item--pending', pending);

  const controls = lineElement.querySelectorAll('.cart-drawer__item-controls input, .cart-drawer__item-controls button');
  for (const control of controls) {
    if (control instanceof HTMLInputElement) {
      // Keep quantity inputs submittable while blocking edits during pending state.
      control.readOnly = pending;
      continue;
    }

    if (control instanceof HTMLButtonElement) {
      control.disabled = pending;
    }
  }

  if (pending) {
    setLineFeedback(lineElement, pendingMessage, 'pending');
    return;
  }

  const feedback = getLineFeedbackElement(lineElement);
  if (feedback?.classList.contains('cart-drawer__line-feedback--pending')) {
    clearLineFeedback(lineElement);
  }
};

const normalizeCartError = (error, messages) => {
  const rawMessage = error instanceof Error ? error.message : `${error || ''}`;
  const normalized = rawMessage.toLowerCase();

  if (normalized.includes('network') || normalized.includes('failed to fetch')) {
    return messages.networkError;
  }

  if (normalized.includes('quantity') && normalized.includes('minimum')) {
    return messages.minimumQuantity.replace('{{ min }}', '0');
  }

  if (normalized.includes('quantity') && normalized.includes('maximum')) {
    return messages.maximumQuantity.replace('{{ max }}', '99');
  }

  if (normalized.includes('sold out') || normalized.includes('unavailable')) {
    return 'This item is unavailable.';
  }

  if (rawMessage.trim().length) {
    return rawMessage;
  }

  return messages.genericError;
};

const validateQuantityInput = (input, messages) => {
  const { quantity, min, max, step } = getQuantityInputValues(input);
  if (!Number.isFinite(quantity)) return messages.invalidNumber;
  if (quantity < min) {
    return messages.minimumQuantity.replace('{{ min }}', `${min}`);
  }

  if (max !== null && quantity > max) {
    return messages.maximumQuantity.replace('{{ max }}', `${max}`);
  }

  if (!Number.isFinite(step) || step <= 0) return null;

  const stepDelta = (quantity - min) / step;
  if (Math.abs(stepDelta - Math.round(stepDelta)) > 0.000001) {
    return messages.invalidStep;
  }

  return null;
};

const extractErrorMessage = async (response, fallbackMessage) => {
  try {
    const payload = await response.json();
    return payload?.description || payload?.message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

const requestDrawerOpen = (id) => {
  document.dispatchEvent(
    new CustomEvent('drawer:open-request', {
      detail: { id },
    })
  );
};

const requestDrawerSync = (id) => {
  document.dispatchEvent(
    new CustomEvent('drawer:sync-request', {
      detail: { id },
    })
  );
};

const replaceHeaderSection = (sectionId, html) => {
  const currentSection = document.getElementById(`shopify-section-${sectionId}`);
  if (!(currentSection instanceof HTMLElement)) return false;

  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(html, 'text/html');
  const nextSection = parsedDocument.getElementById(`shopify-section-${sectionId}`);
  if (!(nextSection instanceof HTMLElement)) return false;

  const currentCount = currentSection.querySelector('[data-cart-count]');
  const nextCount = nextSection.querySelector('[data-cart-count]');
  if (currentCount instanceof HTMLElement && nextCount instanceof HTMLElement) {
    currentCount.textContent = nextCount.textContent;
    currentCount.hidden = nextCount.hidden;
  }

  const currentPanel = currentSection.querySelector(`${CART_DRAWER_SELECTOR} [data-cart-panel]`);
  const nextPanel = nextSection.querySelector(`${CART_DRAWER_SELECTOR} [data-cart-panel]`);
  if (currentPanel instanceof HTMLElement && nextPanel instanceof HTMLElement) {
    currentPanel.replaceChildren(...nextPanel.childNodes);
    return true;
  }

  return false;
};

const refreshCartSection = async (shouldRequestDrawerOpen = false) => {
  const root = getCartDrawerRoot();
  if (!(root instanceof HTMLElement)) return;
  const wasDrawerOpen = getCartDrawer()?.classList.contains('is-open') === true;

  const sectionId = root.dataset.sectionId;
  if (!sectionId) return;

  const url = getStoreUrl(`?sections=${encodeURIComponent(sectionId)}`);
  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Section render failed with status ${response.status}`);
  }

  const payload = await response.json();
  const sectionHtml = payload?.[sectionId];
  if (typeof sectionHtml !== 'string' || !sectionHtml.length) {
    throw new Error('Section render returned invalid payload');
  }

  const replaced = replaceHeaderSection(sectionId, sectionHtml);
  if (!replaced) return;

  if (wasDrawerOpen) {
    requestDrawerSync('cart-drawer');
    return;
  }

  if (shouldRequestDrawerOpen) {
    requestDrawerOpen('cart-drawer');
  }
};

const addToCart = async (form) => {
  const formData = new FormData(form);
  const response = await fetch(getStoreUrl('cart/add.js'), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
    },
    body: formData,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload?.description || payload?.message || 'Unable to add item to cart.');
  }

  return response.json();
};

const updateCart = async (form) => {
  const formData = new FormData(form);
  const body = new URLSearchParams();

  for (const [key, value] of formData.entries()) {
    if (typeof value === 'string') {
      body.append(key, value);
    }
  }

  const response = await fetch(getStoreUrl('cart/update.js'), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    body,
  });

  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, `Cart update failed with status ${response.status}`);
    throw new Error(errorMessage);
  }

  return response.json();
};

const cancelPendingFormUpdate = () => {
  if (autoUpdateTimeoutId) {
    clearTimeout(autoUpdateTimeoutId);
    autoUpdateTimeoutId = null;
  }
  queuedCartUpdateForm = null;
};

const removeCartLine = async (lineItemKey) => {
  if (!lineItemKey) {
    throw new Error('Missing cart line item key');
  }

  const response = await fetch(getStoreUrl('cart/update.js'), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ updates: { [lineItemKey]: 0 } }),
  });

  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, `Cart line removal failed with status ${response.status}`);
    throw new Error(errorMessage);
  }

  return response.json();
};

const setProductFormStatus = (form, message, isError = false) => {
  const status = form.querySelector('[data-product-form-status]');
  if (!(status instanceof HTMLElement)) return;

  status.textContent = message;
  status.hidden = !message;
  status.setAttribute('aria-live', isError ? 'assertive' : 'polite');
};

const runCartFormUpdate = async (form) => {
  const messages = getCartMessages(form);
  clearAllLineFeedback(form);

  const quantityInputs = form.querySelectorAll('[data-cart-quantity-input]');
  const changedLineElements = [];
  let hasValidationErrors = false;

  for (const inputElement of quantityInputs) {
    if (!(inputElement instanceof HTMLInputElement)) continue;

    const lineIndex = Number(inputElement.dataset.lineIndex || '');
    if (!lineIndex) continue;

    const lineElement = getLineElementByIndex(lineIndex, form);
    const validationError = validateQuantityInput(inputElement, messages);
    if (validationError) {
      hasValidationErrors = true;
      setLineFeedback(lineElement, validationError, 'error');
      continue;
    }

    if (Number(inputElement.value) === Number(inputElement.dataset.currentQuantity)) {
      continue;
    }

    if (lineElement instanceof HTMLElement && !changedLineElements.includes(lineElement)) {
      changedLineElements.push(lineElement);
    }
  }

  if (hasValidationErrors) return;
  if (!changedLineElements.length) return;

  for (const lineElement of changedLineElements) {
    setLinePending(lineElement, true, messages.updating);
  }

  try {
    await updateCart(form);
    await refreshCartSection(true);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'update' } }));
  } catch (error) {
    const normalizedError = normalizeCartError(error, messages);
    for (const lineElement of changedLineElements) {
      setLineFeedback(lineElement, normalizedError, 'error');
    }
    console.error(error);
  } finally {
    for (const lineElement of changedLineElements) {
      setLinePending(lineElement, false, messages.updating);
    }
  }
};

const requestCartFormUpdate = async (form) => {
  if (!(form instanceof HTMLFormElement)) return;
  queuedCartUpdateForm = form;
  if (cartUpdateInFlight) return;

  while (queuedCartUpdateForm) {
    const nextForm = queuedCartUpdateForm;
    queuedCartUpdateForm = null;
    cartUpdateInFlight = true;
    await runCartFormUpdate(nextForm);
    cartUpdateInFlight = false;
  }
};

const scheduleCartFormUpdate = (form) => {
  if (!(form instanceof HTMLFormElement)) return;

  if (autoUpdateTimeoutId) {
    clearTimeout(autoUpdateTimeoutId);
  }

  autoUpdateTimeoutId = setTimeout(() => {
    autoUpdateTimeoutId = null;
    requestCartFormUpdate(form);
  }, AUTO_UPDATE_DEBOUNCE_MS);
};

document.addEventListener('click', async (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const removeTrigger = target.closest('[data-cart-remove]');
  if (removeTrigger instanceof HTMLElement) {
    event.preventDefault();
    cancelPendingFormUpdate();
    const form = getCartUpdateForm();
    const messages = getCartMessages(form);
    const lineElement = removeTrigger.closest(CART_LINE_SELECTOR);
    const lineItemKey = lineElement?.dataset.lineItemKey || removeTrigger.dataset.lineItemKey || '';
    if (!lineItemKey) return;

    try {
      clearLineFeedback(lineElement);
      removeTrigger.setAttribute('aria-disabled', 'true');
      setLinePending(lineElement, true, messages.updating);
      await removeCartLine(lineItemKey);
      await refreshCartSection(true);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'remove' } }));
    } catch (error) {
      setLineFeedback(lineElement, normalizeCartError(error, messages), 'error');
      console.error(error);
    } finally {
      setLinePending(lineElement, false, messages.updating);
      removeTrigger.removeAttribute('aria-disabled');
    }
  }
});

document.addEventListener('submit', async (event) => {
  const target = event.target;
  if (!(target instanceof HTMLFormElement)) return;
  const submitter = event.submitter;

  if (target.matches('form.product-form__form')) {
    if (submitter instanceof HTMLElement && submitter.closest('.shopify-payment-button')) {
      return;
    }

    event.preventDefault();

    const submitButton = target.querySelector('[type="submit"]');
    if (submitButton instanceof HTMLButtonElement || submitButton instanceof HTMLInputElement) {
      submitButton.disabled = true;
    }

    try {
      setProductFormStatus(target, '');
      await addToCart(target);
      await refreshCartSection(true);
      const addedMessage = target.querySelector('[data-added-message]')?.textContent?.trim() || 'Added to cart.';
      setProductFormStatus(target, addedMessage);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'add' } }));
    } catch (error) {
      setProductFormStatus(target, error instanceof Error ? error.message : 'Unable to add item to cart.', true);
      console.error(error);
    } finally {
      if (submitButton instanceof HTMLButtonElement || submitButton instanceof HTMLInputElement) {
        submitButton.disabled = false;
      }
    }

    return;
  }

  if (target.matches('form[data-cart-update-form]')) {
    if (submitter instanceof HTMLButtonElement && submitter.name === 'checkout') {
      return;
    }

    event.preventDefault();
    await requestCartFormUpdate(target);
  }
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.matches('[data-cart-quantity-input]')) return;

  const form = target.closest('form[data-cart-update-form]');
  scheduleCartFormUpdate(form);
});

document.addEventListener('keydown', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.matches('[data-cart-quantity-input]')) return;
  if (event.key !== 'Enter') return;

  event.preventDefault();
  const form = target.closest('form[data-cart-update-form]');
  requestCartFormUpdate(form);
});

document.addEventListener('cart:section-refresh-request', async () => {
  try {
    await refreshCartSection(false);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'add' } }));
  } catch (error) {
    console.error(error);
  }
});
